import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase.ts';
import { formatCurrency, formatDate, formatMonthYear } from '../../utils/formatters.ts';

interface TenantBillsProps {
  tenantId: string;
}

const TenantBills: React.FC<TenantBillsProps> = ({ tenantId }) => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBills = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('month', { ascending: false });

      if (error) throw error;
      setBills(data || []);
    } catch (error) {
      console.error('Error fetching bills:', error);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'status-paid';
      case 'unpaid':
        return 'status-unpaid';
      case 'partial':
        return 'status-partial';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const handleBillClick = (billId: string) => {
    navigate(`/bills/${billId}`);
  };

  if (loading) return <div>Loading bills...</div>;

  return (
    <div className="tenant-bills">
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Amount</th>
            <th>Due Date</th>
            <th>Status</th>
            <th>Paid On</th>
          </tr>
        </thead>
        <tbody>
          {bills.map(bill => (
            <tr 
              key={bill.id}
              onClick={() => handleBillClick(bill.id)}
              style={{ cursor: 'pointer' }}
              className="bill-row"
            >
              <td>{formatMonthYear(bill.month)}</td>
              <td>{formatCurrency(bill.amount)}</td>
              <td>{formatDate(bill.due_date)}</td>
              <td>
                <span className={`status-badge ${getStatusClass(bill.status)}`}>
                  <span className="status-dot"></span>
                  {bill.status}
                </span>
              </td>
              <td>
                {bill.status === 'Paid' ? (
                  formatDate(bill.payment_date)
                ) : bill.status === 'Partial' ? (
                  <span className="partial-payment">Partially paid</span>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {bills.length === 0 && <p className="no-data">No bills found</p>}
    </div>
  );
};

export default TenantBills;
